<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { 
    X, RefreshCw, AlertTriangle, CheckCircle2, ShieldCheck, 
    Globe, MapPin, Layers, Radio, Send, TrendingUp, TrendingDown,
    Building2, Activity, Zap, CheckCircle, ExternalLink, Sliders, HelpCircle
  } from "lucide-svelte";
  import 'leaflet/dist/leaflet.css';
  import 'leaflet.markercluster/dist/MarkerCluster.css';
  import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
  import GlassCard from "./ui/GlassCard.svelte";
  import EPAWaterScoreModal from "./EPAWaterScoreModal.svelte";
  import { unitPreference } from "../stores/preferences";

  export interface PortfolioPropertyBenchmark {
    benchmark_id: string;
    property_id: number;
    property_name: string;
    region_market: string;
    latitude: number;
    longitude: number;
    total_units: number;
    total_monitored_devices: number;
    current_flow_rate_gpm: number;
    gallons_per_unit_day: number;
    portfolio_benchmark_percentile: number;
    monthly_budget_variance_pct: number;
    active_anomaly_count: number;
    health_status: 'optimal' | 'advisory' | 'critical_alarm' | string;
    fleet_policy_sync_status: string;
    updated_at: string;
  }

  export interface PortfolioGISOverview {
    selected_region: string;
    available_regions: string[];
    total_properties: number;
    total_monitored_units: number;
    total_portfolio_flow_rate_gpm: number;
    avg_gallons_per_unit_day: number;
    fleet_budget_variance_pct: number;
    fleet_active_alarms: number;
    properties: PortfolioPropertyBenchmark[];
  }

  const defaultPortfolioOverview: PortfolioGISOverview = {
    selected_region: 'all',
    available_regions: ['All Regions', 'Southern California', 'Pacific Northwest', 'Texas Tri-City'],
    total_properties: 5,
    total_monitored_units: 700,
    total_portfolio_flow_rate_gpm: 101.7,
    avg_gallons_per_unit_day: 146.4,
    fleet_budget_variance_pct: -2.4,
    fleet_active_alarms: 1,
    properties: [
      {
        benchmark_id: 'bm-700',
        property_id: 700,
        property_name: 'South Tower',
        region_market: 'Southern California',
        latitude: 34.052235,
        longitude: -118.243683,
        total_units: 240,
        total_monitored_devices: 96,
        current_flow_rate_gpm: 28.4,
        gallons_per_unit_day: 132.5,
        portfolio_benchmark_percentile: 91,
        monthly_budget_variance_pct: -6.8,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized',
        updated_at: '2026-09-26T12:00:00Z'
      },
      {
        benchmark_id: 'bm-706',
        property_id: 706,
        property_name: 'Fedora',
        region_market: 'Southern California',
        latitude: 34.053982,
        longitude: -118.298190,
        total_units: 110,
        total_monitored_devices: 44,
        current_flow_rate_gpm: 14.2,
        gallons_per_unit_day: 148.0,
        portfolio_benchmark_percentile: 78,
        monthly_budget_variance_pct: 1.2,
        active_anomaly_count: 1,
        health_status: 'advisory',
        fleet_policy_sync_status: 'synchronized',
        updated_at: '2026-09-26T12:00:00Z'
      },
      {
        benchmark_id: 'bm-714',
        property_id: 714,
        property_name: 'Hollywood Villas',
        region_market: 'Southern California',
        latitude: 34.093488,
        longitude: -118.316582,
        total_units: 85,
        total_monitored_devices: 34,
        current_flow_rate_gpm: 11.5,
        gallons_per_unit_day: 124.0,
        portfolio_benchmark_percentile: 95,
        monthly_budget_variance_pct: -11.4,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized',
        updated_at: '2026-09-26T12:00:00Z'
      },
      {
        benchmark_id: 'bm-715',
        property_id: 715,
        property_name: 'Phoenix Apartments',
        region_market: 'Southern California',
        latitude: 34.032993,
        longitude: -118.292474,
        total_units: 160,
        total_monitored_devices: 64,
        current_flow_rate_gpm: 34.8,
        gallons_per_unit_day: 188.5,
        portfolio_benchmark_percentile: 42,
        monthly_budget_variance_pct: 18.5,
        active_anomaly_count: 3,
        health_status: 'critical_alarm',
        fleet_policy_sync_status: 'pending_sync',
        updated_at: '2026-09-26T12:00:00Z'
      },
      {
        benchmark_id: 'bm-745',
        property_id: 745,
        property_name: '1841 Stoner Ave',
        region_market: 'Southern California',
        latitude: 34.038696,
        longitude: -118.453143,
        total_units: 95,
        total_monitored_devices: 38,
        current_flow_rate_gpm: 12.8,
        gallons_per_unit_day: 139.0,
        portfolio_benchmark_percentile: 84,
        monthly_budget_variance_pct: -3.2,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized',
        updated_at: '2026-09-26T12:00:00Z'
      }
    ]
  };

  interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    customProperties?: PortfolioPropertyBenchmark[];
  }

  let { isOpen = $bindable(false), onClose, customProperties }: Props = $props();

  let loading = $state(false);
  let actionLoading = $state<string | null>(null);
  let overview = $state<PortfolioGISOverview | null>(defaultPortfolioOverview);
  let selectedRegion = $state("all");
  let errorMsg = $state("");
  let broadcastSuccess = $state<any | null>(null);
  let showScoreModal = $state(false);
  let selectedBenchmarkProp = $state<PortfolioPropertyBenchmark | null>(null);
  const isMetric = $derived($unitPreference === 'metric');

  const effectiveProperties = $derived(
    customProperties && customProperties.length > 0 
      ? customProperties 
      : (overview?.properties || defaultPortfolioOverview.properties)
  );

  const effectiveOverview = $derived.by(() => {
    if (customProperties && customProperties.length > 0) {
      const props = customProperties;
      const totalProps = props.length;
      const totalUnits = props.reduce((acc, p) => acc + (p.total_units || 0), 0);
      const flowGpm = props.reduce((acc, p) => acc + (p.current_flow_rate_gpm || 0), 0);
      const avgGud = totalProps ? (props.reduce((acc, p) => acc + (p.gallons_per_unit_day || 0), 0) / totalProps) : 0;
      const budgetVar = totalProps ? (props.reduce((acc, p) => acc + (p.monthly_budget_variance_pct || 0), 0) / totalProps) : 0;
      const alarms = props.reduce((acc, p) => acc + (p.active_anomaly_count || 0), 0);
      return {
        selected_region: overview?.selected_region || 'all',
        available_regions: overview?.available_regions || ['All Regions'],
        total_properties: totalProps,
        total_monitored_units: totalUnits,
        total_portfolio_flow_rate_gpm: Math.round(flowGpm * 10) / 10,
        avg_gallons_per_unit_day: Math.round(avgGud * 10) / 10,
        fleet_budget_variance_pct: Math.round(budgetVar * 10) / 10,
        fleet_active_alarms: alarms,
        properties: props
      };
    }
    return overview || defaultPortfolioOverview;
  });

  // Policy Broadcast Form State
  let selectedPolicyType = $state("freeze_guard_lockout");
  let broadcastTargetRegion = $state("Southern California");
  let broadcastOfficer = $state("Enterprise Operations Director");

  // Map state
  let mapElement: HTMLElement | null = $state(null);
  let mapInstance: any = null;
  let leafletLib: any = null;
  let markers: any[] = [];

  const policyLabels: Record<string, string> = {
    freeze_guard_lockout: "Emergency Freeze Guard Setpoint Lockout",
    drought_stage_sync: "Municipal Drought Stage Harmonization",
    high_flow_auto_shutoff: "Fleetwide High-Flow Automatic Shutoff Protocol"
  };

  function getWaterStressInfo(region: string) {
    if (region?.includes('California') || region?.includes('SoCal')) {
      return { stress: 'Stage 2 Drought', rate: '$11.40 / kGal', badge: 'badge-warning' };
    } else if (region?.includes('Texas')) {
      return { stress: 'Moderate Stress', rate: '$8.20 / kGal', badge: 'badge-info' };
    } else if (region?.includes('Vancouver') || region?.includes('Pacific Northwest')) {
      return { stress: 'Low Stress', rate: '$5.40 / kGal', badge: 'badge-ghost' };
    }
    return { stress: 'Normal', rate: '$7.50 / kGal', badge: 'badge-ghost' };
  }

  async function loadOverview(region: string = selectedRegion) {
    loading = true;
    errorMsg = "";
    try {
      const res = await fetch(`/api/portfolio/gis-overview?region=${encodeURIComponent(region)}`);
      if (!res.ok) {
        throw new Error(`Failed to load portfolio overview: ${res.statusText}`);
      }
      const data: PortfolioGISOverview = await res.json();
      overview = data;
      if (typeof window !== "undefined" && mapInstance && leafletLib) {
        renderMapMarkers();
      }
    } catch (err: any) {
      console.error(err);
      errorMsg = err.message || "Failed to load GIS data.";
    } finally {
      loading = false;
    }
  }

  async function initLeaflet() {
    if (typeof window === "undefined" || !mapElement) return;
    try {
      const L = await import("leaflet");
      leafletLib = L.default || L;

      // Always tear down any existing map instance before mounting to current element
      if (mapInstance) {
        try {
          mapInstance.remove();
        } catch {
          // ignore
        }
        mapInstance = null;
      }

      if (mapElement) {
        mapInstance = leafletLib.map(mapElement, {
          center: [34.0522, -118.2437],
          zoom: 9,
          zoomControl: true,
        });

        leafletLib.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 18,
        }).addTo(mapInstance);

        renderMapMarkers();
      }
    } catch (e) {
      console.warn("Leaflet initialization fallback:", e);
    }
  }

  function renderMapMarkers() {
    if (!mapInstance || !leafletLib) return;

    // Clear existing markers safely
    markers.forEach(m => {
      try {
        mapInstance.removeLayer(m);
      } catch {
        // ignore
      }
    });
    markers = [];

    const propsToRender = effectiveProperties;
    if (!propsToRender || propsToRender.length === 0) return;

    const bounds: [number, number][] = [];

    propsToRender.forEach(p => {
      if (!p.latitude || !p.longitude) return;

      bounds.push([p.latitude, p.longitude]);

      const pinColor = p.health_status === 'optimal' 
        ? '#10b981' 
        : p.health_status === 'advisory' 
        ? '#f59e0b' 
        : '#ef4444';

      const gisIcon = leafletLib.divIcon({
        className: 'custom-gis-modal-icon',
        html: `
          <div style="background: ${pinColor}; color: white; border-radius: 9999px; padding: 3px 8px; font-weight: 800; font-size: 11px; box-shadow: 0 4px 14px rgba(0,0,0,0.35); border: 2px solid white; display: flex; align-items: center; gap: 5px; white-space: nowrap; transform: translate(-50%, -50%); cursor: pointer; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">
            <span style="width: 7px; height: 7px; border-radius: 50%; background: #ffffff; display: inline-block;"></span>
            <span>${p.portfolio_benchmark_percentile}th %ile</span>
            <span style="opacity: 0.9; font-weight: 600; font-size: 10px;">${isMetric ? (p.current_flow_rate_gpm * 3.78541).toFixed(1) + ' L/min' : p.current_flow_rate_gpm.toFixed(1) + ' GPM'}</span>
          </div>
        `,
        iconSize: [120, 24],
        iconAnchor: [60, 12]
      });

      const marker = leafletLib.marker([p.latitude, p.longitude], {
        icon: gisIcon
      }).addTo(mapInstance);

      marker.bindPopup(`
        <div class="p-2 space-y-1.5 text-xs min-w-[210px]">
          <div class="flex items-center justify-between gap-1">
            <span class="badge badge-sm font-black ${p.portfolio_benchmark_percentile >= 90 ? 'badge-primary' : p.portfolio_benchmark_percentile >= 75 ? 'badge-success' : 'badge-warning'}">
              EPA WaterScore ${p.portfolio_benchmark_percentile}th %ile
            </span>
            <span class="text-[10px] text-slate-500 font-semibold uppercase">${p.region_market}</span>
          </div>
          <div class="font-bold text-sm text-slate-900">${p.property_name}</div>
          <div class="text-slate-600">${p.total_units} Monitored Units</div>
          <div class="grid grid-cols-2 gap-1.5 p-2 bg-slate-100 rounded-xl text-[11px] my-1 text-slate-800">
            <div>
              <span class="text-slate-500 block text-[9px] uppercase font-bold">Current Flow</span>
              <span class="font-mono font-bold text-sky-600">${isMetric ? (p.current_flow_rate_gpm * 3.78541).toFixed(1) + ' L/min' : p.current_flow_rate_gpm.toFixed(1) + ' GPM'}</span>
            </div>
            <div>
              <span class="text-slate-500 block text-[9px] uppercase font-bold">${isMetric ? 'Daily LUD' : 'Daily GUD'}</span>
              <span class="font-mono font-bold text-emerald-600">${isMetric ? (p.gallons_per_unit_day * 3.78541).toFixed(1) + ' L' : p.gallons_per_unit_day.toFixed(1) + ' gal'}</span>
            </div>
            <div>
              <span class="text-slate-500 block text-[9px] uppercase font-bold">Budget Var.</span>
              <span class="font-mono font-bold ${p.monthly_budget_variance_pct <= 0 ? 'text-emerald-600' : 'text-amber-600'}">
                ${p.monthly_budget_variance_pct > 0 ? '+' : ''}${p.monthly_budget_variance_pct.toFixed(1)}%
              </span>
            </div>
            <div>
              <span class="text-slate-500 block text-[9px] uppercase font-bold">Status</span>
              <span class="font-bold uppercase text-[10px] ${p.health_status === 'optimal' ? 'text-emerald-600' : p.health_status === 'advisory' ? 'text-amber-600' : 'text-rose-600'}">
                ${p.health_status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      `);

      markers.push(marker);
    });

    if (bounds.length > 0) {
      if (bounds.length === 1) {
        mapInstance.setView(bounds[0], 14);
      } else {
        mapInstance.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }

  async function handleBroadcastPolicy() {
    actionLoading = "broadcast";
    broadcastSuccess = null;
    errorMsg = "";
    try {
      const res = await fetch("/api/portfolio/broadcast-policy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          policy_type: selectedPolicyType,
          target_region: broadcastTargetRegion,
          broadcast_by: broadcastOfficer,
          parameters: {
            dispatched_at: new Date().toISOString(),
            enforce_immediate_sync: true
          }
        })
      });

      if (!res.ok) {
        throw new Error(`Policy broadcast failed: ${res.statusText}`);
      }

      const result = await res.json();
      broadcastSuccess = result;

      // Refresh overview data
      await loadOverview();
    } catch (err: any) {
      console.error(err);
      errorMsg = err.message || "Failed to broadcast fleet policy.";
    } finally {
      actionLoading = null;
    }
  }

  $effect(() => {
    if (isOpen) {
      if (!customProperties || customProperties.length === 0) {
        void loadOverview(selectedRegion);
      }
      const t1 = setTimeout(() => {
        void initLeaflet();
      }, 100);
      const t2 = setTimeout(() => {
        if (mapInstance) {
          mapInstance.invalidateSize();
          renderMapMarkers();
        }
      }, 350);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      if (mapInstance) {
        try {
          mapInstance.remove();
        } catch {
          // ignore
        }
        mapInstance = null;
      }
    }
  });

  onDestroy(() => {
    if (mapInstance) {
      try {
        mapInstance.remove();
      } catch {
        // ignore
      }
      mapInstance = null;
    }
  });
</script>

{#if isOpen}
  <!-- Backdrop -->
  <div 
    class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gis-modal-title"
  >
    <div 
      class="bg-base-100 rounded-3xl border border-base-200/80 shadow-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden text-base-content"
    >
      <!-- Modal Header -->
      <div class="p-6 border-b border-base-200/80 flex items-center justify-between bg-base-200/30">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-2xl bg-primary/10 text-primary">
            <Globe class="w-6 h-6" />
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h2 id="gis-modal-title" class="text-xl font-black tracking-tight">
                Enterprise Portfolio GIS Heatmap & Benchmarking
              </h2>
              <span class="badge badge-primary badge-sm font-bold uppercase tracking-wider">
                Fleet Fleetwide
              </span>
            </div>
            <p class="text-xs text-base-content/60 mt-0.5">
              Cross-property macro telemetry, regional GIS clustering, EPA WaterScore percentiles, and instant fleet policy broadcasts.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Region Filter Selector -->
          <div class="join">
            {#each (effectiveOverview?.available_regions || ['All Regions', 'Southern California', 'Pacific Northwest']) as reg}
              <button 
                class="btn btn-sm join-item {selectedRegion === (reg === 'All Regions' ? 'all' : reg) ? 'btn-primary' : 'btn-ghost'}"
                onclick={() => { 
                  const target = reg === 'All Regions' ? 'all' : reg;
                  selectedRegion = target; 
                  loadOverview(target); 
                }}
              >
                {reg === 'All Regions' ? 'All' : reg === 'Southern California' ? 'SoCal' : reg === 'Pacific Northwest' ? 'PacNW' : reg.replace('Greater ', '')}
              </button>
            {/each}
          </div>

          <button 
            class="btn btn-circle btn-sm btn-ghost"
            onclick={() => loadOverview()}
            title="Refresh Fleet Data"
            aria-label="Refresh Fleet Data"
          >
            <RefreshCw class="w-4 h-4 {loading ? 'animate-spin' : ''}" />
          </button>

          <button 
            class="btn btn-circle btn-sm btn-ghost"
            onclick={() => { isOpen = false; onClose?.(); }}
            aria-label="Close modal"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto space-y-6 flex-1">
        {#if errorMsg}
          <div class="alert alert-error text-xs rounded-2xl shadow-sm">
            <AlertTriangle class="w-4 h-4" />
            <span>{errorMsg}</span>
          </div>
        {/if}

        {#if broadcastSuccess}
          <div class="alert alert-success text-xs rounded-2xl shadow-sm flex items-center justify-between">
            <div class="flex items-center gap-2">
              <CheckCircle2 class="w-4 h-4" />
              <span>
                <strong>Fleet Policy Broadcast Synchronized!</strong> 
                Dispatched {policyLabels[broadcastSuccess.policy_type] || broadcastSuccess.policy_type} across {broadcastSuccess.properties_targeted} properties in {broadcastSuccess.target_region}.
                (Receipt ID: <code>{broadcastSuccess.broadcast_id.slice(0, 8)}</code>)
              </span>
            </div>
            <button class="btn btn-xs btn-ghost" onclick={() => broadcastSuccess = null}>Dismiss</button>
          </div>
        {/if}

        <!-- Macro Fleet KPI Ribbon -->
        {#if effectiveOverview}
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Properties</span>
              <span class="text-xl font-black text-primary mt-1 block">{effectiveOverview.total_properties}</span>
              <span class="text-[10px] text-base-content/40">Active Managed Sites</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Monitored Units</span>
              <span class="text-xl font-black text-base-content mt-1 block">{effectiveOverview.total_monitored_units}</span>
              <span class="text-[10px] text-base-content/40">Residences & Spaces</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Flow Rate</span>
              <span class="text-xl font-black text-info mt-1 block">
                {isMetric ? (effectiveOverview.total_portfolio_flow_rate_gpm * 3.78541).toFixed(1) : effectiveOverview.total_portfolio_flow_rate_gpm.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L/min' : 'GPM'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">Instantaneous Total</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Avg Fleet {isMetric ? 'LUD' : 'GUD'}</span>
              <span class="text-xl font-black text-emerald-500 mt-1 block">
                {isMetric ? (effectiveOverview.avg_gallons_per_unit_day * 3.78541).toFixed(1) : effectiveOverview.avg_gallons_per_unit_day.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L' : 'gal'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">{isMetric ? 'Liters' : 'Gallons'} / Unit / Day</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Budget Variance</span>
              <span class="text-xl font-black mt-1 block {effectiveOverview.fleet_budget_variance_pct <= 0 ? 'text-success' : 'text-warning'}">
                {effectiveOverview.fleet_budget_variance_pct > 0 ? '+' : ''}{effectiveOverview.fleet_budget_variance_pct.toFixed(1)}%
              </span>
              <span class="text-[10px] text-base-content/40">vs Monthly Baseline</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Active Fleet Alarms</span>
              <span class="text-xl font-black mt-1 block {effectiveOverview.fleet_active_alarms === 0 ? 'text-success' : 'text-error'}">
                {effectiveOverview.fleet_active_alarms}
              </span>
              <span class="text-[10px] text-base-content/40">Cross-Site Anomaly Sentry</span>
            </div>
          </div>
        {/if}

        <!-- Interactive GIS Map Section -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div class="lg:col-span-8 bg-base-200/40 rounded-3xl p-4 border border-base-200/80 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <MapPin class="w-4 h-4 text-primary" />
                <h3 class="font-bold text-sm tracking-tight">Geographic Spatial Asset Distribution</h3>
              </div>
              <div class="flex items-center gap-3 text-[11px] font-medium text-base-content/60">
                <span class="flex items-center gap-1">
                  <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span> Optimal
                </span>
                <span class="flex items-center gap-1">
                  <span class="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block"></span> Advisory
                </span>
                <span class="flex items-center gap-1">
                  <span class="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block"></span> Critical Alarm
                </span>
              </div>
            </div>

            <div 
              bind:this={mapElement} 
              class="w-full rounded-2xl bg-base-300 relative overflow-hidden shadow-inner border border-base-200/60"
              style="min-height: 380px; height: 380px;"
              data-testid="gis-map-container"
            >
              {#if !effectiveProperties?.length}
                <div class="absolute inset-0 flex items-center justify-center text-xs text-base-content/40">
                  Loading map coordinates...
                </div>
              {/if}
            </div>
          </div>

          <!-- 1-Click Fleet Policy Broadcast Action Box -->
          <div class="lg:col-span-4 bg-base-200/40 rounded-3xl p-5 border border-base-200/80 flex flex-col justify-between">
            <div>
              <div class="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-1">
                <Radio class="w-4 h-4" />
                Unified Fleet Policy Engine
              </div>
              <h4 class="font-black text-sm tracking-tight text-base-content/90">
                Synchronized Broadcast Dispatch
              </h4>
              <p class="text-xs text-base-content/60 mt-1">
                Distribute instantaneous operational setpoints or emergency shutoff safeguards across all portfolio properties simultaneously.
              </p>

              <div class="space-y-3 mt-4">
                <div>
                  <label class="text-[10px] font-bold uppercase tracking-wider text-base-content/60 block mb-1">
                    Select Fleet Policy
                  </label>
                  <select 
                    class="select select-bordered select-sm w-full rounded-xl text-xs"
                    bind:value={selectedPolicyType}
                    data-testid="policy-type-select"
                  >
                    <option value="freeze_guard_lockout">Emergency Freeze Guard Setpoint Lockout</option>
                    <option value="drought_stage_sync">Municipal Drought Stage Harmonization</option>
                    <option value="high_flow_auto_shutoff">Fleetwide High-Flow Auto-Shutoff Override</option>
                  </select>
                </div>

                <div>
                  <label class="text-[10px] font-bold uppercase tracking-wider text-base-content/60 block mb-1">
                    Target Regional Scope
                  </label>
                  <select 
                    class="select select-bordered select-sm w-full rounded-xl text-xs"
                    bind:value={broadcastTargetRegion}
                    data-testid="target-region-select"
                  >
                    <option value="all">All Regional Markets (Fleetwide)</option>
                    <option value="Southern California">Southern California Region</option>
                    <option value="Pacific Northwest">Pacific Northwest Region</option>
                  </select>
                </div>

                <div>
                  <label class="text-[10px] font-bold uppercase tracking-wider text-base-content/60 block mb-1">
                    Authorizing Officer
                  </label>
                  <input 
                    type="text" 
                    class="input input-bordered input-sm w-full rounded-xl text-xs"
                    bind:value={broadcastOfficer}
                    placeholder="Officer Name"
                  />
                </div>
              </div>
            </div>

            <button 
              class="btn btn-primary btn-sm w-full rounded-xl gap-2 mt-4 font-bold shadow-md"
              onclick={handleBroadcastPolicy}
              disabled={actionLoading === 'broadcast'}
              data-testid="broadcast-policy-button"
            >
              {#if actionLoading === 'broadcast'}
                <span class="loading loading-spinner loading-xs"></span>
                <span>Broadcasting to Fleet...</span>
              {:else}
                <Send class="w-4 h-4" />
                <span>Broadcast Policy to Fleet</span>
              {/if}
            </button>
          </div>
        </div>

        <!-- Cross-Property Macro Benchmarking Table -->
        <div class="bg-base-200/40 rounded-3xl p-5 border border-base-200/80 space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="font-bold text-sm tracking-tight">Macro Property Benchmarking Leaderboard</h3>
              <p class="text-xs text-base-content/60">
                Sorted by EPA WaterScore efficiency percentile. Compares baseline pacing across multi-family assets.
              </p>
            </div>
            <div class="badge badge-outline badge-sm font-semibold">
              {effectiveProperties?.length || 0} Benchmarked Sites
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="table table-sm w-full text-xs">
              <thead>
                <tr class="border-b border-base-200/80 text-[10px] uppercase tracking-wider text-base-content/50">
                  <th>Rank / WaterScore</th>
                  <th>Property & Market</th>
                  <th>Units</th>
                  <th>Current Flow</th>
                  <th>GUD (gal/unit/day)</th>
                  <th>Budget Variance</th>
                  <th>Water Stress & Rate</th>
                  <th>Health Status</th>
                  <th>Fleet Sync</th>
                </tr>
              </thead>
              <tbody>
                {#if effectiveProperties}
                  {#each effectiveProperties as prop, idx}
                    {@const stress = getWaterStressInfo(prop.region_market)}
                    <tr class="hover:bg-base-200/60 transition-colors">
                      <td class="font-bold">
                        <div class="flex items-center gap-1.5">
                          <span class="badge badge-sm font-black {prop.portfolio_benchmark_percentile >= 90 ? 'badge-primary' : prop.portfolio_benchmark_percentile >= 80 ? 'badge-success' : 'badge-ghost'}">
                            #{idx + 1}
                          </span>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 font-semibold {prop.portfolio_benchmark_percentile < 60 ? 'text-error' : prop.portfolio_benchmark_percentile < 75 ? 'text-warning' : 'text-emerald-600 dark:text-emerald-400'} hover:underline cursor-pointer"
                            title="View EPA WaterScore calculation for {prop.property_name}"
                            onclick={() => { selectedBenchmarkProp = prop; showScoreModal = true; }}
                          >
                            <span>{prop.portfolio_benchmark_percentile}th %ile</span>
                            <HelpCircle class="w-3 h-3 opacity-60" />
                          </button>
                        </div>
                      </td>
                      <td>
                        <div class="font-bold text-base-content/90">{prop.property_name}</div>
                        <div class="text-[10px] text-base-content/50 flex items-center gap-1">
                          <MapPin class="w-3 h-3" />
                          {prop.region_market}
                        </div>
                      </td>
                      <td class="font-semibold">{prop.total_units}</td>
                      <td>
                        <span class="font-mono font-semibold">{isMetric ? (prop.current_flow_rate_gpm * 3.78541).toFixed(1) : prop.current_flow_rate_gpm.toFixed(1)}</span>
                        <span class="text-[10px] text-base-content/50">{isMetric ? 'L/min' : 'GPM'}</span>
                      </td>
                      <td>
                        <span class="font-mono font-bold {prop.gallons_per_unit_day < 50 ? 'text-emerald-500' : 'text-base-content'}">
                          {isMetric ? (prop.gallons_per_unit_day * 3.78541).toFixed(1) : prop.gallons_per_unit_day.toFixed(1)}
                        </span>
                        <span class="text-[10px] text-base-content/50">{isMetric ? 'L' : 'gal'}</span>
                      </td>
                      <td>
                        <span class="badge badge-sm font-bold {prop.monthly_budget_variance_pct <= 0 ? 'badge-success badge-outline' : 'badge-warning badge-outline'}">
                          {prop.monthly_budget_variance_pct > 0 ? '+' : ''}{prop.monthly_budget_variance_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <span class="badge badge-xs {stress.badge} font-bold">{stress.stress}</span>
                        <div class="text-[10px] text-base-content/60 font-mono mt-0.5">{stress.rate}</div>
                      </td>
                      <td>
                        {#if prop.health_status === 'optimal'}
                          <span class="badge badge-success badge-sm gap-1 font-bold">
                            <CheckCircle class="w-3 h-3" /> Optimal
                          </span>
                        {:else if prop.health_status === 'advisory'}
                          <span class="badge badge-warning badge-sm gap-1 font-bold">
                            <AlertTriangle class="w-3 h-3" /> Advisory
                          </span>
                        {:else}
                          <span class="badge badge-error badge-sm gap-1 font-bold animate-pulse">
                            <AlertTriangle class="w-3 h-3" /> Alarm ({prop.active_anomaly_count})
                          </span>
                        {/if}
                      </td>
                      <td>
                        <span class="badge badge-ghost badge-sm text-[10px] capitalize font-medium">
                          {prop.fleet_policy_sync_status}
                        </span>
                      </td>
                    </tr>
                  {/each}
                {:else}
                  <tr>
                    <td colspan="9" class="text-center py-8 text-base-content/40">
                      Loading benchmarked properties...
                    </td>
                  </tr>
                {/if}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="p-4 border-t border-base-200/80 bg-base-200/30 flex items-center justify-between">
        <div class="text-[11px] text-base-content/50 flex items-center gap-1.5">
          <ShieldCheck class="w-3.5 h-3.5 text-success" />
          <span>Synchronized with Enterprise Operations API & TimescaleDB GIS cluster</span>
        </div>
        <button 
          class="btn btn-sm btn-ghost rounded-xl font-bold"
          onclick={() => { isOpen = false; onClose?.(); }}
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <EPAWaterScoreModal
    isOpen={showScoreModal}
    onClose={() => { showScoreModal = false; selectedBenchmarkProp = null; }}
    benchmark={selectedBenchmarkProp}
    property={selectedBenchmarkProp}
    totalUnits={selectedBenchmarkProp?.total_units || 0}
    activeAlertsCount={selectedBenchmarkProp?.active_anomaly_count || 0}
  />
{/if}
